import { BasicDropdown } from "@frontend/components/input/BasicDropdown";
import {
  Select,
  SelectContent,
  SelectItemRightCheck,
  SelectTrigger,
  SelectValue,
} from "@frontend/shared/components/ui/select";
import { cn } from "@frontend/shared/lib/utils";
import { useTranslations } from "next-intl";
import type React from "react";
import type {
  ControllerRenderProps,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import type { z } from "zod";
import { formatRequiredLabel } from "./IntakeFormQuestion";
import type { singleSelectFieldSchema } from "./IntakeFormTypes";

const IntakeSingleSelectInput = ({
  hiddenQuestions,
  setHiddenQuestions,
  updateProgress,
  field,
  question,
  register,
  getValues,
  setValue,
  isAdmin,
  isPrint,
  initiateAutosave,
  disabled,
  useNewStyles,
  valueChangeCallback,
  hideQuestionText,
}: {
  hiddenQuestions: Set<number>;
  setHiddenQuestions: React.Dispatch<React.SetStateAction<Set<number>>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  updateProgress: (updatedField: string, updatedValue: any) => void;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  field: ControllerRenderProps<Partial<any>, string>;
  question: z.infer<typeof singleSelectFieldSchema>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  register: any;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  getValues: UseFormGetValues<Partial<any>>;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  setValue: UseFormSetValue<Partial<any>>;
  isAdmin: boolean;
  isPrint: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  initiateAutosave: (data: Partial<any>, hiddenQuestions?: Set<number>) => void;
  disabled?: boolean;
  useNewStyles?: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  valueChangeCallback?: (value: any, questionId: number) => void;
  hideQuestionText?: boolean;
}) => {
  const choices = question.questionAttributes.choices;

  if (!choices) {
    return null;
  }

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const updateHiddenQuestions = (choice: any | undefined): Set<number> => {
    const oldValue = getValues(field.name);
    const tempHiddenQuestions = new Set(hiddenQuestions);
    const oldChoice = choices.find((c) => c.id === oldValue);

    // order matters: Remove then add in case both choices have the same hidden question

    if (oldChoice?.conditionalQuestions) {
      for (const q of oldChoice.conditionalQuestions) {
        tempHiddenQuestions.add(q);
      }
    }

    if (choice?.conditionalQuestions) {
      for (const q of choice.conditionalQuestions) {
        tempHiddenQuestions.delete(q);
      }
    }

    //@ts-ignore potential bug in NextJS build time
    const newlyHiddenQuestions = new Set(
      [...tempHiddenQuestions].filter((q) => !hiddenQuestions.has(q)),
    );

    for (const field of newlyHiddenQuestions) {
      setValue(field.toString(), "");
    }

    setHiddenQuestions(tempHiddenQuestions);
    return tempHiddenQuestions;
  };

  const choiceDropdownData = choices.map((c) => ({
    ...c,
    label: c.value,
  }));

  const selectedValue = getValues(field.name);

  const t = useTranslations("IntakeFormStates");
  const showCheckboxes = useNewStyles
    ? choices.length < 10
    : choices.length < 4;

  return (
    <div className="flex flex-col w-full gap-1" key={question.id}>
      {showCheckboxes || isPrint ? (
        <div>
          <p
            className="text-sm text-[#000000]"
            data-pdflabel="singleInput-label"
            data-questionid={question.id}
          >
            {formatRequiredLabel(
              question.questionText,
              question.isRequired,
              hideQuestionText,
            )}
          </p>
          <div className="w-full min-h-12 flex gap-2 sm:flex-col lg:flex-row lg:flex-wrap print:flex-row">
            {choices.map((choice, i) => (
              <label
                key={choice.id}
                className={cn(
                  "transition-all duration-200 w-full sm:max-w-full print:max-w-[180px]  items-center flex gap-3  pl-3 sm:py-3 lg:py-0 print:py-0 cursor-pointer rounded-[4px]",
                  useNewStyles
                    ? "rounded-full p-4 bg-[#F4F4F4] justify-between border border-transparent md:h-16 md:px-7"
                    : "border",
                  field.value === choice.id &&
                    field.value !== undefined &&
                    useNewStyles &&
                    "bg-white border-black border",
                  isAdmin &&
                    choice.critical &&
                    selectedValue === choice.value &&
                    "bg-yellow-300",
                )}
              >
                {useNewStyles && (
                  <p
                    className="text-sm font-medium"
                    data-pdflabel="singleInput-choice"
                    data-questionid={question.id}
                    data-choiceindex={i}
                  >
                    {choice.value}
                  </p>
                )}
                <input
                  {...field}
                  {...register(field.name)}
                  value={choice.id}
                  data-questionid={question.id}
                  data-pdflabel="singleInput-radio"
                  data-choiceindex={i}
                  type="radio"
                  onClick={() => {
                    const newValue =
                      getValues(field.name) === choice.id ? undefined : choice;

                    const updatedHiddenQuestions =
                      updateHiddenQuestions(newValue);
                    setValue(field.name, newValue?.id ?? "");
                    if (newValue?.id) {
                      valueChangeCallback?.(newValue?.id, question.id);
                    }
                  }}
                  className={
                    useNewStyles
                      ? "w-6 h-6 border-black border rounded-full appearance-none checked:bg-white checked:border-1 checked:border-black checked:before:content-[''] checked:before:absolute checked:before:w-3.5 checked:before:h-3.5 checked:before:bg-black checked:before:rounded-full checked:before:left-1/2 checked:before:top-1/2 checked:before:-translate-x-1/2 checked:before:-translate-y-1/2 relative"
                      : ""
                  }
                  disabled={isAdmin || disabled}
                />
                {!useNewStyles && (
                  <p
                    className="text-sm font-light"
                    data-pdflabel="singleInput-choice"
                    data-questionid={question.id}
                    data-choiceindex={i}
                  >
                    {choice.value}
                  </p>
                )}
              </label>
            ))}
          </div>
        </div>
      ) : !useNewStyles ? (
        <BasicDropdown
          data={choiceDropdownData}
          data-pdflabel="dropdown-label"
          valuePlaceholderText={t("SelectAnOption")}
          valueText="option"
          selectedValue={choiceDropdownData.find((x) => x.id === selectedValue)}
          labelText={formatRequiredLabel(
            question.questionText,
            question.isRequired,
            hideQuestionText,
          )}
          onSelectValue={(datum) => {
            const newValue =
              getValues(field.name) === datum.id ? undefined : datum;

            const updatedHiddenQuestions = updateHiddenQuestions(newValue);
            setValue(field.name, newValue?.id);
            updateProgress(field.name, newValue?.id);
            initiateAutosave(
              { ...getValues(), [field.name]: newValue?.id },
              updatedHiddenQuestions,
            );
          }}
          pdfquestion={question}
          disabled={isAdmin || disabled}
        />
      ) : (
        <div className="flex flex-col gap-1">
          <label
            htmlFor={`select-${question.id}`}
            className="text-sm text-[#000000]"
          >
            {formatRequiredLabel(
              question.questionText,
              question.isRequired,
              hideQuestionText,
            )}
          </label>
          <Select
            value={selectedValue?.toString()}
            onValueChange={(value) => {
              const datum = choiceDropdownData.find(
                (x) => x.id.toString() === value,
              );
              const newValue =
                getValues(field.name) === datum?.id ? undefined : datum;

              const updatedHiddenQuestions = updateHiddenQuestions(newValue);
              setValue(field.name, newValue?.id);
              updateProgress(field.name, newValue?.id);
              initiateAutosave(
                { ...getValues(), [field.name]: newValue?.id },
                updatedHiddenQuestions,
              );
            }}
            disabled={isAdmin || disabled}
          >
            <SelectTrigger
              className={cn(
                "w-full rounded-full p-6  font-medium ",
                field.value ? "bg-white border-black border" : "",
              )}
            >
              <SelectValue placeholder={t("SelectAnOption")} />
            </SelectTrigger>
            <SelectContent className="rounded-3xl py-2">
              {choiceDropdownData.map((choice) => (
                <SelectItemRightCheck
                  className="px-6 rounded-2xl font-light"
                  key={choice.id}
                  value={choice.id.toString()}
                >
                  {choice.label}
                </SelectItemRightCheck>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default IntakeSingleSelectInput;
